import React from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Box, Typography, Link, useMediaQuery, Theme } from "@mui/material";
import { GainLossPercents, LoadingPlaceHolder } from "@components";
import { useMandatorLinks } from "@hooks";
import { useCommunityPosts } from "./useCommunityPosts";

const POSTS_LIMIT = 6;

const Wrapper = styled(Box)({
  width: "100%",
});

const PostItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(3, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-of-type": {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const RankBadge = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "18px",
  height: "18px",
  minWidth: "18px",
  borderRadius: "50%",
  backgroundColor: theme.palette.sidebar.avatarOnsidebar,
  color:
    theme.type === "wso"
      ? theme.palette.text.onColorDark
      : theme.palette.text.onColorLight,
  marginRight: theme.spacing(3),
}));

const PostInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
});

export const CommunityPosts = ({ topPadding }: { topPadding?: number }) => {
  const { t } = useTranslation();
  const isSM = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { topics, isLoading, isEmpty } = useCommunityPosts(POSTS_LIMIT);
  const { communityDiscussionsUrl } = useMandatorLinks();

  if (!communityDiscussionsUrl) return null;

  return (
    <Wrapper pt={topPadding}>
      <Typography variant={isSM ? "body4" : "body2"} mb={2}>
        {t("dashboardPage.dashboard.communityPosts.title")}
      </Typography>
      <Box mb={6}>
        {isLoading && <LoadingPlaceHolder height="250px" />}
        {isEmpty && (
          <Typography variant="body4" color="textSecondary" py={3}>
            {t("dashboardPage.dashboard.communityPosts.noPosts")}
          </Typography>
        )}
        {!isLoading &&
          topics?.map((topic, index) => (
            <PostItem key={`${topic.target}-${index}`}>
              <RankBadge>
                <Typography variant="overline2">{index + 1}</Typography>
              </RankBadge>
              <PostInfo>
                <Link href={topic.target} underline="hover">
                  <Typography variant="body4" fontWeight={600} noWrap>
                    {topic.title}
                  </Typography>
                </Link>
              </PostInfo>
              {topic.perfRel !== undefined && (
                <Box ml={2}>
                  <GainLossPercents value={topic.perfRel / 100} size="small" />
                </Box>
              )}
            </PostItem>
          ))}
      </Box>
      <Link href={communityDiscussionsUrl} underline="hover" display="block">
        <Typography variant="body4">
          {t("dashboardPage.dashboard.communityPosts.allDiscussions")}
        </Typography>
      </Link>
    </Wrapper>
  );
};
